import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, MessageCircle, Globe } from 'lucide-react';
import AdminNav, { SECTIONS, isActive } from './AdminNav';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Shell de l'administration (Mission 3, Lot 7) — menu UNIQUE partagé (`AdminNav`),
// identique à celui du cockpit Pilotage. Plus de double administration.
export default function AdminLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [msgCount, setMsgCount] = useState(0);

  // Auth guard
  useEffect(() => {
    const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
    if (!token) { navigate('/connexion'); return; }
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const pad = base64 + '='.repeat((4 - base64.length % 4) % 4);
      const payload = JSON.parse(atob(pad));
      if (payload.role !== 'admin') navigate('/');
    } catch { navigate('/connexion'); }
  }, [navigate]);

  // Badge messages (topbar)
  useEffect(() => {
    const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
    if (!token) return;
    fetch(`${BACKEND}/api/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setMsgCount(d.count || 0)).catch(() => {});
  }, []);

  const currentLabel = (() => {
    if (location.pathname === '/admin') return 'Tableau de bord';
    for (const s of SECTIONS)
      for (const item of s.items)
        if (item.path && isActive(item.path, false, location)) return item.label;
    return 'Admin';
  })();

  return (
    <div data-theme="admin-dark" className="flex h-screen overflow-hidden" style={{ background: 'var(--admin-bg)' }}>

      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-60 flex-shrink-0 overflow-hidden">
        <AdminNav />
      </aside>

      {/* Sidebar mobile (overlay) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden"
          style={{ background: 'rgba(4,6,9,.7)', backdropFilter: 'blur(2px)' }}
          onClick={() => setSidebarOpen(false)}>
          <aside className="w-64 h-full" onClick={(e) => e.stopPropagation()}>
            <AdminNav onNavigate={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Contenu principal */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Topbar */}
        <header className="flex items-center gap-3 flex-shrink-0"
          style={{
            background: 'rgba(10,13,17,.85)', backdropFilter: 'blur(14px)',
            borderBottom: '1px solid var(--admin-border-soft)', padding: '12px 18px', minHeight: 52,
          }}>
          <button className="lg:hidden flex-shrink-0" onClick={() => setSidebarOpen(true)}
            style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid var(--admin-border)',
              background: 'var(--admin-surface)', color: 'var(--admin-text)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            data-testid="admin-burger">
            <Menu size={16}/>
          </button>

          <h1 className="flex-1 min-w-0 truncate text-sm font-semibold"
            style={{ color: 'var(--admin-text)', fontFamily: 'var(--admin-font-display)',
              letterSpacing: '-.2px', fontSize: 16 }}>
            {currentLabel}
          </h1>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 text-xs"
              style={{ color: 'var(--admin-muted)', border: '1px solid var(--admin-border)',
                padding: '6px 10px', borderRadius: 9, background: 'var(--admin-surface)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--admin-green)',
                boxShadow: '0 0 0 3px var(--admin-green-dim)', display: 'inline-block' }} />
              En direct
            </div>
            <Link to="/admin/messages"
              style={{ position: 'relative', padding: 8, borderRadius: 9,
                background: msgCount > 0 ? 'var(--admin-teal-dim)' : 'transparent',
                color: msgCount > 0 ? 'var(--admin-teal)' : 'var(--admin-muted)' }}>
              <MessageCircle size={16}/>
              {msgCount > 0 && (
                <span style={{ position: 'absolute', top: -2, right: -2, width: 16, height: 16,
                  borderRadius: 16, background: 'var(--admin-teal)', color: '#000', fontSize: 9,
                  fontWeight: 700, display: 'grid', placeItems: 'center' }}>
                  {msgCount}
                </span>
              )}
            </Link>
            <a href="/" target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-xs rounded-lg flex-shrink-0"
              style={{ background: 'var(--admin-surface)', color: 'var(--admin-muted)',
                border: '1px solid var(--admin-border)', textDecoration: 'none', padding: '8px 10px' }}
              data-testid="admin-voir-site-btn" title="Voir le site public">
              <Globe size={15}/>
              <span className="hidden sm:inline">Voir le site</span>
            </a>
            <Link to="/admin"
              className="flex items-center gap-2 rounded-lg flex-shrink-0"
              style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)',
                textDecoration: 'none', padding: '4px 6px' }}
              data-testid="admin-account-badge" title={`${user?.nom || 'Admin'} — ${user?.email || ''}`}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)', fontFamily: 'var(--admin-font-display)' }}>
                {(user?.nom || user?.email || 'A')[0].toUpperCase()}
              </div>
              <div className="hidden lg:block" style={{ minWidth: 0, maxWidth: 130 }}>
                <p className="text-xs font-semibold truncate" style={{ color: 'var(--admin-text)' }}>{user?.nom || 'Admin'}</p>
                <p className="truncate" style={{ color: 'var(--admin-faint)', fontSize: 10 }}>{user?.email}</p>
              </div>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" style={{ padding: '20px 18px 60px' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
